import axios from "axios";

// Local backend URL
// export const BASE_URL = "http://127.0.0.1:5002";

// Production backend URL
export const BASE_URL = "http://20.193.142.182:5002";

const API_BASE_URL = `${BASE_URL}/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Prevent multiple logout processes from running together
let isLoggingOut = false;

// Stores the automatic logout timer
let autoLogoutTimerId = null;

// Stores the refresh token for which the timer was created
let scheduledForRefreshToken = null;

/**
 * Get the expiry time from a JWT token.
 *
 * JWT stores its expiry time in the "exp" field.
 * The returned value is in milliseconds.
 */
function decodeJwtExpiryMs(token) {
  try {
    const payloadPart = token.split(".")[1];

    if (!payloadPart) {
      return null;
    }

    // Convert Base64 URL format to normal Base64 format
    const normalizedPayload = payloadPart
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    // Add missing Base64 padding
    const paddedPayload = normalizedPayload.padEnd(
      Math.ceil(normalizedPayload.length / 4) * 4,
      "="
    );

    const decodedPayload = decodeURIComponent(
      atob(paddedPayload)
        .split("")
        .map((character) => {
          return `%${character
            .charCodeAt(0)
            .toString(16)
            .padStart(2, "0")}`;
        })
        .join("")
    );

    const payload = JSON.parse(decodedPayload);

    return payload.exp ? payload.exp * 1000 : null;
  } catch (error) {
    console.error("Unable to decode JWT token:", error);
    return null;
  }
}

/**
 * Clear the automatic logout timer.
 */
function clearScheduledAutoLogout() {
  if (autoLogoutTimerId) {
    clearTimeout(autoLogoutTimerId);
    autoLogoutTimerId = null;
  }

  scheduledForRefreshToken = null;
}

/**
 * Log the user out and redirect to the login page.
 */
async function forceLogoutAndExpireSession() {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  const expiredToken = localStorage.getItem("token");

  /*
   * Notify the backend that the session has expired.
   * This request is optional and will not prevent logout if it fails.
   */
  if (expiredToken) {
    try {
      await axios.post(
        `${API_BASE_URL}/auth/session-expire`,
        {},
        {
          headers: {
            Authorization: `Bearer ${expiredToken}`,
          },
        }
      );
    } catch (cleanupError) {
      console.warn(
        "Could not notify the server about session expiry:",
        cleanupError
      );
    }
  }

  clearScheduledAutoLogout();

  // Remove token, refresh token, user and all other saved session data
  localStorage.clear();

  // "/" is the login route in your App.js
  window.location.href = "/?expired=true";
}

/**
 * Create a timer that logs the user out exactly when the refresh
 * token expires.
 */
function scheduleAutoLogoutFromStorage() {
  const refreshToken = localStorage.getItem("refresh_token");

  if (!refreshToken) {
    clearScheduledAutoLogout();
    return;
  }

  // Do not create another timer for the same refresh token
  if (refreshToken === scheduledForRefreshToken) {
    return;
  }

  const expiresAtMs = decodeJwtExpiryMs(refreshToken);

  /*
   * If the stored refresh token is malformed, remove the session.
   * A valid JWT refresh token must contain an expiry time.
   */
  if (!expiresAtMs) {
    forceLogoutAndExpireSession();
    return;
  }

  if (autoLogoutTimerId) {
    clearTimeout(autoLogoutTimerId);
  }

  scheduledForRefreshToken = refreshToken;

  const millisecondsUntilExpiry = expiresAtMs - Date.now();
  // const millisecondsUntilExpiry = Math.min(expiresAtMs - Date.now(), 60 * 1000);

  // The session has already expired
  if (millisecondsUntilExpiry <= 0) {
    forceLogoutAndExpireSession();
    return;
  }

  autoLogoutTimerId = setTimeout(() => {
    forceLogoutAndExpireSession();
  }, millisecondsUntilExpiry);
}

// Run the session check whenever the application loads
scheduleAutoLogoutFromStorage();

/**
 * Request interceptor
 *
 * Adds the access token to every request.
 */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    /*
     * This also detects when a new refresh token has been saved
     * after login or token rotation.
     */
    scheduleAutoLogoutFromStorage();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 *
 * 1. Detects an expired access token.
 * 2. Tries to obtain new access and refresh tokens.
 * 3. Repeats the original API request.
 * 4. Redirects to login if the complete session has expired.
 */
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config || {};

    if (error.response) {
      const statusCode = error.response.status;

      const isLoginRequest =
        originalRequest.url?.includes("/auth/login");

      const isRefreshRequest =
        originalRequest.url?.includes("/auth/refresh");

      /*
       * Do not intercept an incorrect username/password response.
       * Otherwise, a failed login request would cause an unnecessary
       * session-expired redirect.
       */
      if (
        statusCode === 401 &&
        !originalRequest._retry &&
        !isLoginRequest &&
        !isRefreshRequest
      ) {
        originalRequest._retry = true;

        try {
          const refreshToken =
            localStorage.getItem("refresh_token");

          if (!refreshToken) {
            throw new Error("Refresh token is not available");
          }

          const refreshResponse = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
              },
            }
          );

          if (
            refreshResponse.data?.success &&
            refreshResponse.data?.token
          ) {
            const newAccessToken =
              refreshResponse.data.token;

            const newRefreshToken =
              refreshResponse.data.refresh_token;

            // Save the new access token
            localStorage.setItem(
              "token",
              newAccessToken
            );

            // Save the rotated refresh token, if returned
            if (newRefreshToken) {
              localStorage.setItem(
                "refresh_token",
                newRefreshToken
              );
            }

            // Create a new logout timer for the rotated token
            scheduleAutoLogoutFromStorage();

            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${newAccessToken}`,
            };

            // Repeat the request that originally failed
            return api(originalRequest);
          }

          throw new Error(
            "Backend did not return a valid refreshed token"
          );
        } catch (refreshError) {
          /*
           * Refresh token is missing, invalid or expired.
           * The full login session is over.
           */
          await forceLogoutAndExpireSession();

          return Promise.reject(refreshError);
        }
      }

      /*
       * The request was already retried with a new token but the
       * backend still returned 401. The session is no longer valid.
       */
      if (
        statusCode === 401 &&
        originalRequest._retry &&
        !isLoginRequest
      ) {
        await forceLogoutAndExpireSession();
      }

      if (statusCode === 500) {
        console.error(
          "Internal Server Error. Please try again later."
        );
      }
    } else if (error.request) {
      console.error(
        "Network error. Please check your internet connection."
      );
    }

    return Promise.reject(error);
  }
);

export default api;